import { inject, Injectable } from '@angular/core';
import { combineLatest, first, map, mergeMap, Observable, of } from 'rxjs';

import { SchemaService } from '@blaze-shared/schema-data-access';
import { objectAccessDotNotationRegex } from '@blaze-shared/shared-util';
import { isDatasetNode } from '@blaze-shared/workflow-domain';

import { IntelliAutoConfigService } from './intelli-auto-config.service';
import { IntelliAutocompleteService } from './intelli-autocomplete.service';
import { ContextValue } from './models/intelli-autocomplete.models';
import { AutocompleteConfig, IntelliConfig, IntelliValueInfo } from './models/intellisense';

@Injectable({ providedIn: 'root' })
export class IntelliValueService {
  private readonly iAutocompleteService = inject(IntelliAutocompleteService);
  private readonly iAutoConfigService = inject(IntelliAutoConfigService);
  private readonly schemaService = inject(SchemaService);

  /**
   * Converts a simple typed in text value into an autocompleted one if such is found.
   * @param inputValue - text value.
   * @param autocompleteConfig - config used to create a list of autocomplete options from which one may be chosen.
   * @returns
   */
  convertTypedValuesIntoAutocompleted(
    inputValue: string,
    autocompleteConfig?: AutocompleteConfig,
  ): ContextValue | undefined {
    if (autocompleteConfig) {
      const autoOptions = this.iAutocompleteService.createValueOptions(autocompleteConfig);
      const autoOptionsMap = new Map<string, ContextValue>();

      autoOptions.forEach((autoOption) => {
        const key = autoOption.value;
        autoOptionsMap.set(key, autoOption);
        autoOptionsMap.set(this.schemaService.replaceColumnAndTagIds(key), autoOption);
      });

      const columnNameInputValue = this.schemaService.replaceColumnIdsWithNames(inputValue);
      const inputValuesToCompare = new Set([
        inputValue,
        inputValue.replaceAll(`'`, `"`),
        inputValue.replace(objectAccessDotNotationRegex, (m, s) => `["${s}"]`),
        columnNameInputValue,
        columnNameInputValue.replaceAll(`'`, `"`),
        columnNameInputValue.replace(objectAccessDotNotationRegex, (m, s) => `["${s}"]`),
      ]);

      let autoValue: ContextValue | undefined;
      for (const inputValueToCompare of inputValuesToCompare) {
        if (autoOptionsMap.has(inputValueToCompare)) {
          autoValue = autoOptionsMap.get(inputValueToCompare);
        }
      }
      return autoValue;
    }
    return undefined;
  }

  createAutocompleteConfig(
    config: IntelliConfig | null,
    valueInfo$: Observable<IntelliValueInfo | undefined>,
  ): Observable<AutocompleteConfig> {
    // If a node operates on a different schema than the input schema, use the schema on which the node operates
    // to extract fixed values.
    const fixedValuesSchemaId = config?.fixedValuesSchema?.id;
    const nodeDatasetId = isDatasetNode(config?.workflowNode) && config.workflowNode.datasetId;
    const fixedValuesSchema$ =
      fixedValuesSchemaId === nodeDatasetId
        ? of(config?.fixedValuesSchema)
        : this.iAutoConfigService.getNodeDatasetSchema(config);

    return combineLatest([
      this.iAutoConfigService.getContext(),
      combineLatest({
        accountsSchema: this.schemaService.obtainAccountsSchema(),
        fixedValuesSchema: fixedValuesSchema$,
        inputSchema: this.iAutoConfigService.getInputSchema(config),
        usersSchema: this.schemaService.obtainUsersSchema(),
        valueInfo: valueInfo$,
      }).pipe(first()),
    ]).pipe(
      mergeMap(([context, { accountsSchema, fixedValuesSchema, usersSchema, valueInfo }]) => {
        config = config && accountsSchema ? { ...config, accountsSchema } : config;
        config = config && fixedValuesSchema ? { ...config, fixedValuesSchema } : config;
        config = config && usersSchema ? { ...config, usersSchema } : config;
        config = config && valueInfo ? { ...config, valueInfo } : config;
        return combineLatest({
          accountColumns: this.iAutoConfigService.getAccountColumns(config),
          context: of(context),
          datasetColumns: this.iAutoConfigService.getDatasetReferenceColumns(config),
          schema: this.iAutoConfigService.getInputSchema(config),
          tagValues: this.iAutoConfigService.getTagValuesFromSchema(config),
          userColumns: this.iAutoConfigService.getUserColumns(config, context),
          valuesFromColumn: this.iAutoConfigService.getValuesFromColumn(config),
        }).pipe(first());
      }),
      map(
        ({ accountColumns, context, datasetColumns, tagValues, userColumns, valuesFromColumn }) => {
          const autocompleteConfig: AutocompleteConfig = {
            accountColumns: accountColumns,
            column: config?.column,
            columnTypes: config?.columnTypes,
            context: context,
            currentRowColumns: this.iAutoConfigService.getCurrentRowColumns(config),
            currentRowInnerColumns: this.iAutoConfigService.getCurrentRowInnerColumns(config),
            datasetColumns: datasetColumns,
            disableCategoryLabel: config?.disableCategoryLabel ?? false,
            formulas: this.iAutoConfigService.getFormulas(config),
            hideDataTypeLabels: config?.hideDataTypeLabels ?? false,
            inputSchema: config?.inputSchema,
            namedInputsColumns: this.iAutoConfigService.getNamedInputsColumns(config),
            operator: config?.operator,
            pageContext: this.iAutoConfigService.getPageContext(config),
            predefinedValues: this.iAutoConfigService.getPredefinedAutocompleteValues(config),
            schemaColumns: this.iAutoConfigService.getSchemaColumns(config, datasetColumns),
            tagValues: tagValues,
            urlVars: this.iAutoConfigService.getUrlVars(config, context),
            userColumns: userColumns,
            valuesFromColumn: valuesFromColumn,
            showFixedValuesForLinks: config?.showFixedValuesForLinks,
            vars: this.iAutoConfigService.getVars(config, context),
            workflowNode: config?.workflowNode,
          };
          return autocompleteConfig;
        },
      ),
    );
  }

  isSpace(text: string): boolean {
    return text === ' ' || (text.length === 1 && text.charCodeAt(0) === 160);
  }

  isTextNode(node: Node | null | undefined): boolean | null | undefined {
    return node && node.nodeName === '#text';
  }
}
